// Terms of use and privacy notice from the brand pack (FR-73), in the
// language the person is reading in. The Markdown is rendered by the small
// safe renderer: nothing in the document is ever interpreted as HTML.
import { Loader, Paper, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { unwrap } from "../../shared/session/errors";
import { Markdown } from "../../shared/ui/Markdown";
import { FormError } from "../../shared/ui/Page";

type Doc = "terms" | "privacy";
type Locale = "en" | "sw";

const LOCALES: readonly string[] = ["en", "sw"] satisfies Locale[];

export function LegalPage({ doc }: { doc: Doc }) {
  const { t, i18n } = useTranslation();
  const locale = (LOCALES.includes(i18n.language) ? i18n.language : "en") as Locale;
  const legal = useQuery({
    queryKey: ["public", "legal", doc, locale],
    queryFn: () => unwrap(api.GET("/public/legal/{doc}", { params: { path: { doc }, query: { locale } } })),
  });
  const title = doc === "terms" ? t("legal.terms_title") : t("legal.privacy_title");

  return (
    <Stack maw={760} mx="auto" py="xl" gap="md" data-testid={`legal-${doc}`}>
      {legal.isSuccess ? (
        <Paper withBorder p="lg" radius="md" component="article" lang={legal.data.locale}>
          <Stack gap="md">
            <Markdown source={legal.data.markdown} />
            <Text size="sm" c="dimmed" data-testid="legal-version">
              {t("legal.version", { version: legal.data.version })}
            </Text>
          </Stack>
        </Paper>
      ) : (
        <>
          <Title order={1} size="h2">
            {title}
          </Title>
          {legal.isPending ? (
            <Loader size="sm" aria-label={t("common.loading")} />
          ) : (
            <FormError message={t("legal.unavailable")} />
          )}
        </>
      )}
    </Stack>
  );
}

export function TermsPage() {
  return <LegalPage doc="terms" />;
}

export function PrivacyPage() {
  return <LegalPage doc="privacy" />;
}
